import React, { useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Instagram,
    Mail,
    Phone,
    Play,
    Camera,
    Video,
    Utensils,
    Sparkles,
    Clapperboard,
    MessageCircle,
    Check,
    MapPin,
    Film,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useState } from "react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFab from "@/components/WhatsAppFab";
import StickyCTA from "@/components/StickyCTA";
import RecentWork from "@/components/RecentWork";
import SystemVersionBadge from "@/components/SystemVersionBadge";
import InternationalCollabBlock from "@/components/InternationalCollabBlock";
import { useT } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSTAGRAM_URL = "https://www.instagram.com/kaily_gan/";
const WHATSAPP_NUMBER = "60183628663"; // international, no +
const WHATSAPP_DISPLAY = "+60 18-362 8663";
const EMAIL = "kailygan.my@gmail.com";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hi Kaily, I'd like to know more about your shoot services for my F&B business."
)}`;

// Services + Process data were previously hardcoded in English — 2026-10-08
// moved to functions so each card/step pulls text from the i18n dictionary.
// The icon + accent stay in code (visual tokens, not copy).
const SERVICES_META = [
    { id: "brand-film", key: "brand_film", icon: Clapperboard, accent: "from-cafe-espresso/0 via-cafe-espresso/40 to-cafe-espresso" },
    { id: "commercial-campaign", key: "commercial", icon: Camera, accent: "from-cafe-espresso/0 via-cafe-espresso/40 to-cafe-espresso" },
    { id: "product-video", key: "product", icon: Video, accent: "from-cafe-espresso/0 via-cafe-espresso/40 to-cafe-espresso" },
    { id: "social-reels", key: "reels", icon: Sparkles, accent: "from-cafe-espresso/0 via-cafe-espresso/40 to-cafe-espresso" },
    { id: "food-photography", key: "food", icon: Utensils, accent: "from-cafe-espresso/0 via-cafe-espresso/40 to-cafe-espresso" },
];

const buildServices = (t) => SERVICES_META.map((m) => ({
    id: m.id, icon: m.icon, accent: m.accent,
    title: t(`services.card.${m.key}.title`),
    short: t(`services.card.${m.key}.short`),
    details: [
        t(`services.card.${m.key}.d1`),
        t(`services.card.${m.key}.d2`),
        t(`services.card.${m.key}.d3`),
    ],
}));

const buildProcess = (t) => [
    { step: "01", title: t("process.step.1.title"), body: t("process.step.1.body") },
    { step: "02", title: t("process.step.2.title"), body: t("process.step.2.body") },
    { step: "03", title: t("process.step.3.title"), body: t("process.step.3.body") },
    { step: "04", title: t("process.step.4.title"), body: t("process.step.4.body") },
];

const CLIENT_TAGS = [
    "Specialty cafes",
    "Brunch spots",
    "Bakeries",
    "Bistros",
    "Pastry chefs",
    "Roasters",
];

// ---------- Hero ----------
function Hero() {
    const { t, lang } = useT();
    // Pull Kaily's uploaded hero media (image + optional video) from the CMS.
    // We keep local state so the page remains usable if site-meta is slow or
    // offline — fallback is the editorial olive placeholder (no play icon).
    //
    // Hero image has TWO independent variants — desktop and mobile — each
    // with its own file and its own object-position crop. We render both
    // <img> tags and let CSS swap them at the md breakpoint; this keeps the
    // page SSR-friendly and avoids layout flicker when the user resizes.
    // If only one variant is uploaded we fall back to it on all screens so
    // Kaily can roll out gradually.
    const [meta, setMeta] = useState(null);
    useEffect(() => {
        axios.get(`${API}/site-meta`).then((r) => setMeta(r.data)).catch(() => setMeta({}));
    }, []);
    const heroImgDesktop = meta?.hero_image || "";
    const heroImgMobile = meta?.hero_image_mobile || "";
    // 2026-10-08 — only treat the legacy `hero_video_url` as a playable
    // <video src> when it actually points at a video file. Historical
    // records sometimes stored an Instagram post page URL
    // (https://www.instagram.com/p/…), which an HTML5 <video> cannot play
    // and which silently blocked the mobile/desktop playback copies from
    // ever rendering (the empty <video> covered everything). The regex
    // allowlists .mp4 / .webm / .mov / .m4v and our own R2 CDN.
    const isPlayableVideoUrl = (u) => {
        if (!u) return false;
        const low = u.toLowerCase();
        if (/\.(mp4|webm|mov|m4v)(\?|#|$)/.test(low)) return true;
        if (low.includes("r2.dev/") || low.includes("r2.cloudflarestorage.com")) return true;
        return false;
    };
    const heroVideo = isPlayableVideoUrl(meta?.hero_video_url) ? meta.hero_video_url : "";
    // Prefer the ffmpeg-produced web copy (+faststart, broadly compatible)
    // and fall back to the raw original if transcoding is still pending or
    // disabled on this environment. If both are empty we fall through to
    // the still photo.
    const heroVideoMobile = meta?.hero_video_playback_mobile || meta?.hero_video_url_mobile || "";
    const heroVideoDesktop = meta?.hero_video_playback_desktop || meta?.hero_video_url_desktop || "";
    const heroVideoMobileCrop = meta?.hero_video_mobile_crop || { x: 50, y: 50 };
    const heroVideoDesktopCrop = meta?.hero_video_desktop_crop || { x: 50, y: 50 };
    const heroPoster = meta?.hero_video_poster || heroImgDesktop || heroImgMobile || "";
    const heroCropDesktop = meta?.hero_image_crop || { x: 50, y: 50 };
    const heroCropMobile = meta?.hero_image_mobile_crop || { x: 50, y: 50 };
    // Effective per-breakpoint sources (one may be empty — then fall back
    // to the other so a single upload still shows everywhere).
    const srcDesktop = heroImgDesktop || heroImgMobile;
    const srcMobile = heroImgMobile || heroImgDesktop;
    const cropDesktop = heroImgDesktop ? heroCropDesktop : heroCropMobile;
    const cropMobile = heroImgMobile ? heroCropMobile : heroCropDesktop;
    const hasAnyImage = !!(srcDesktop || srcMobile);
    const hasMobileVideo = !!heroVideoMobile;
    const hasDesktopVideo = !!heroVideoDesktop;
    // Visual DNA — matches Kaily's approved mockup:
    //  - huge Fraunces display headline that spans full container width
    //  - the second line sits INSIDE the olive film block (negative margin / z-index overlap)
    //  - italic right-aligned sub-caption
    //  - olive block holds the "YOUR ORIGINAL FILM" play affordance (hidden if no real reel)
    //  - thin eyebrow + bottom caption rail flank the olive block
    const headlineL1 = lang === "cn" ? "电影感" : "CINEMATIC";
    const headlineL2 = lang === "cn" ? "品牌影片" : "BRAND FILMS";
    const subCaption = lang === "cn" ? "食物。感受。氛围。" : "Food. Feeling. Atmosphere.";
    const bodyLine = lang === "cn"
        ? "为餐饮与款待品牌打造的影片与视觉叙事。"
        : "Brand films and visual storytelling for food & hospitality.";
    const malaysiaLine = lang === "cn"
        ? "马来西亚出发。欢迎"
        : "Based in Malaysia. Available for ";
    const malaysiaLinkLabel = lang === "cn" ? "国际合作" : "international collaborations";
    const malaysiaLineTail = lang === "cn" ? "。" : ".";

    return (
        <section id="top" className="relative pt-24 md:pt-28 pb-10 md:pb-14 overflow-hidden" data-testid="hero-section">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14">
                {/* Eyebrow */}
                <div className="flex items-center justify-between gap-6" data-testid="hero-overline">
                    <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75">
                        {lang === "cn" ? "马来西亚 & 海外" : "KAILY GAN / MALAYSIA & BEYOND"}
                    </p>
                    <p className="hidden md:block font-display italic text-cafe-espresso/85 text-xl lg:text-2xl leading-none">
                        {subCaption}
                    </p>
                </div>

                {/* Headline + olive film block — two layers overlap by design */}
                <div className="relative mt-4 md:mt-6">
                    {/* Line 1 — above the olive block.
                        2026-02 — Chinese characters (12vw) rendered taller than Latin
                        (11vw) and were being clipped at the top on mobile because
                        the olive block had `overflow-hidden` on all breakpoints
                        and the Line-2 overlap pulled the baseline up. The clip
                        is addressed in two places:
                        (1) Line-1 line-height is bumped to ~1.0 on mobile so the
                            ascender fits inside its own box.
                        (2) The olive block only turns on `overflow-hidden` from
                            md: upward (desktop editorial crop). On mobile the
                            Line-2 cream text is rendered as a sibling that can
                            spill above the olive box without being cut. */}
                    <h1
                        className={[
                            "relative z-20 text-cafe-espresso leading-[0.98] md:leading-[0.86] tracking-[-0.015em] whitespace-nowrap pt-[0.08em]",
                            lang === "cn" ? "font-serif-cn" : "font-serif",
                        ].join(" ")}
                        data-testid="hero-headline-line1"
                        style={{ fontSize: lang === "cn" ? "clamp(2.5rem, 12vw, 11rem)" : "clamp(2.5rem, 11vw, 13rem)", fontWeight: 500 }}
                    >
                        {headlineL1}
                    </h1>

                    {/* Olive block — content well + play affordance (or real reel).
                        Mobile aspect switches to 9:16 when Kaily uploaded a
                        portrait reel so the native video frame renders without
                        letterboxing. Photo-only mode keeps the editorial 16:18.
                        2026-10-08 mobile fix — the olive block used to pull up
                        by -0.14em on mobile, which combined with the Line 2
                        `-translate-y-[0.16em]` + `overflow-hidden` wrapper to
                        chop the top ~16 % off every character on 360-430 px
                        screens ("BRAND FILMS" / "品牌影片" readability bug).
                        Mobile now overlaps less (-0.04em) and keeps the
                        editorial cut only from md: upwards. */}
                    <div
                        className={[
                            "relative mt-[-0.04em] md:mt-[-0.18em] bg-cafe-olive overflow-hidden",
                            hasMobileVideo ? "aspect-[9/16]" : "aspect-[16/18]",
                            "sm:aspect-[16/13] md:aspect-[16/8] lg:aspect-[16/7]",
                        ].join(" ")}
                        data-testid="hero-film-block"
                    >
                        {/* Backdrop media — video wins over image if set.
                            Both are faded slightly to keep the headline legible.
                            Images render as a pair (desktop vs mobile) and CSS
                            swaps them at the md breakpoint. The original file
                            is NEVER re-encoded, stretched or tone-shifted —
                            we only reframe via object-position. */}
                        {/* 2026-10-08 — prefer device-specific playback. The
                            legacy single `hero_video_url` is used ONLY when
                            Kaily has NOT uploaded a mobile or desktop reel
                            (so old content from the single-video era still
                            renders), AND when the URL is a real video file.
                            Otherwise we drop into the mobile+desktop branch
                            where each viewport gets its own <video>. */}
                        {heroVideo && !heroVideoMobile && !heroVideoDesktop ? (
                            <video
                                src={heroVideo}
                                poster={heroPoster || undefined}
                                autoPlay muted loop playsInline
                                className="absolute inset-0 w-full h-full object-cover"
                                data-testid="hero-video"
                            />
                        ) : (
                            <>
                                {/* MOBILE media — <768px. Vertical reel if
                                    uploaded, otherwise the mobile photo.
                                    Photo always renders underneath as the
                                    poster/fallback so a failed autoplay or a
                                    broken video still shows Kaily's work. */}
                                <div className="absolute inset-0 md:hidden">
                                    {srcMobile && (
                                        <img
                                            src={srcMobile}
                                            alt=""
                                            className="absolute inset-0 w-full h-full object-cover"
                                            style={{ objectPosition: `${cropMobile.x}% ${cropMobile.y}%` }}
                                            data-testid="hero-image-mobile"
                                        />
                                    )}
                                    {hasMobileVideo && (
                                        <video
                                            src={heroVideoMobile}
                                            poster={srcMobile || undefined}
                                            autoPlay muted loop playsInline
                                            preload="metadata"
                                            className="absolute inset-0 w-full h-full object-cover"
                                            style={{ objectPosition: `${heroVideoMobileCrop.x}% ${heroVideoMobileCrop.y}%` }}
                                            data-testid="hero-video-mobile"
                                        />
                                    )}
                                </div>
                                {/* DESKTOP media — md+. Still photo renders
                                    first (confirmed layout); the optional
                                    reel overlays on top as a progressive
                                    enhancement when uploaded. If autoplay
                                    is blocked (reduced-motion, data saver)
                                    the photo stays visible underneath. */}
                                {srcDesktop && (
                                    <img
                                        src={srcDesktop}
                                        alt=""
                                        className="absolute inset-0 w-full h-full object-cover hidden md:block"
                                        style={{ objectPosition: `${cropDesktop.x}% ${cropDesktop.y}%` }}
                                        data-testid="hero-image"
                                    />
                                )}
                                {hasDesktopVideo && (
                                    <video
                                        src={heroVideoDesktop}
                                        poster={srcDesktop || undefined}
                                        autoPlay muted loop playsInline
                                        preload="metadata"
                                        className="absolute inset-0 w-full h-full object-cover hidden md:block"
                                        style={{ objectPosition: `${heroVideoDesktopCrop.x}% ${heroVideoDesktopCrop.y}%` }}
                                        data-testid="hero-video-desktop"
                                    />
                                )}
                            </>
                        )}
                        {/* Dark overlay for text legibility when media is present */}
                        {(heroVideo || hasAnyImage || hasMobileVideo || hasDesktopVideo) && (
                            <div className="absolute inset-0 bg-gradient-to-t from-cafe-olive/80 via-cafe-olive/35 to-cafe-olive/20" />
                        )}

                        {/* Line 2 sits INSIDE the olive block in cream — the signature overlap.
                            2026-02 — Chinese top-strokes were being clipped
                            because the Line-2 wrapper used `top-0` + overflow
                            cropping, which eats the character's actual top
                            ascender. Fix on mobile: push Line-2 DOWN by ~6%
                            of the font size (via `pt-[0.08em]`) so the top
                            stroke sits comfortably inside the olive block,
                            and keep `overflow-hidden` only on md:+ (the
                            editorial crop stays as-is on desktop). */}
                        <div className="absolute inset-x-0 top-0 flex items-start justify-center md:overflow-hidden pointer-events-none">
                            <span
                                className={[
                                    "text-cafe-cream leading-[0.98] md:leading-[0.86] tracking-[-0.015em] md:-translate-y-[0.16em] whitespace-nowrap pt-[0.1em] md:pt-0",
                                    lang === "cn" ? "font-serif-cn" : "font-serif",
                                ].join(" ")}
                                style={{ fontSize: lang === "cn" ? "clamp(2.5rem, 12vw, 11rem)" : "clamp(2.5rem, 11vw, 13rem)", fontWeight: 500 }}
                                data-testid="hero-headline-line2"
                            >
                                {headlineL2}
                            </span>
                        </div>

                        {/* Content stack — DESKTOP ONLY (md:+). On mobile this
                            whole block is hidden and the body + CTAs render
                            BELOW the olive video block in the cream area, so
                            the video becomes the visual anchor and the CTAs
                            get real tap room without overlapping the footage.
                            2026-02 Hero rework per Kaily's brief. */}
                        <div className="hidden md:flex relative h-full z-10 flex-col justify-between pb-8 md:pb-12 px-6 pt-28 md:pt-20">
                            {(heroVideo || hasMobileVideo) && (
                                <div className="flex-1 flex items-center justify-center md:items-end md:pb-8">
                                    <p className="text-[10px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-cream/70 text-center max-w-xs">
                                        {lang === "cn" ? "原创品牌影片 · 2026" : "Original brand film reel · 2026"}
                                    </p>
                                </div>
                            )}

                            {/* Body + CTAs stack */}
                            <div className="flex flex-col items-center gap-4 md:gap-5">
                                <p className="text-cafe-cream/90 text-sm md:text-base max-w-xl text-center leading-relaxed">
                                    {bodyLine}
                                </p>
                                <div className="flex items-center gap-3 flex-wrap justify-center">
                                    <Link
                                        to="/work"
                                        data-testid="hero-cta-work"
                                        className="inline-flex items-center h-11 md:h-12 px-6 md:px-7 rounded-full bg-cafe-cream text-cafe-espresso text-[13.5px] font-medium hover:-translate-y-0.5 transition-transform"
                                    >
                                        {lang === "cn" ? "浏览作品" : "View Work"}
                                        <ArrowUpRight className="ml-1.5 h-4 w-4" strokeWidth={1.5} />
                                    </Link>
                                    <a
                                        href="#contact"
                                        data-testid="hero-cta-primary"
                                        className="inline-flex items-center h-11 md:h-12 px-6 md:px-7 rounded-full border border-cafe-cream text-cafe-cream text-[13.5px] font-medium hover:bg-cafe-cream hover:text-cafe-espresso transition-colors"
                                    >
                                        {lang === "cn" ? "开启项目" : "Discuss a Project"}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* MOBILE-only content stack — body + CTAs live BELOW the olive
                    video block, in the cream area, so the video becomes the
                    visual anchor and the CTAs get proper tap room without
                    sitting on top of the footage. Mirrors the desktop block
                    that still lives inside the olive overlay. */}
                <div className="md:hidden mt-5 flex flex-col items-start gap-4">
                    {(heroVideo || hasMobileVideo) && (
                        <p className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/60"
                           data-testid="hero-reel-tag-mobile">
                            {lang === "cn" ? "原创品牌影片 · 2026" : "Original brand film reel · 2026"}
                        </p>
                    )}
                    <p className="text-cafe-espresso/80 text-[15px] leading-relaxed max-w-md">
                        {bodyLine}
                    </p>
                    <div className="flex items-center gap-5 flex-wrap">
                        <Link
                            to="/work"
                            data-testid="hero-cta-work-mobile"
                            className="inline-flex items-center h-11 px-6 rounded-full bg-cafe-espresso text-cafe-cream text-[13.5px] font-medium hover:-translate-y-0.5 transition-transform"
                        >
                            {lang === "cn" ? "浏览作品" : "View Work"}
                            <ArrowUpRight className="ml-1.5 h-4 w-4" strokeWidth={1.5} />
                        </Link>
                        <a
                            href="#contact"
                            data-testid="hero-cta-primary-mobile"
                            className="text-cafe-espresso/80 text-[13.5px] font-medium underline underline-offset-4 decoration-cafe-espresso/30 hover:decoration-cafe-espresso"
                        >
                            {lang === "cn" ? "讨论项目" : "Discuss a project"}
                        </a>
                    </div>
                </div>

                {/* Bottom rail — location + mini meta */}
                <div className="mt-6 md:mt-8 flex items-center justify-center">
                    <p className="text-[12px] md:text-[13px] text-cafe-espresso/60 tracking-wide text-center">
                        {malaysiaLine}
                        <Link
                            to="/international"
                            data-testid="hero-international-link"
                            className="text-cafe-olive underline underline-offset-2 decoration-cafe-olive/40 hover:decoration-cafe-olive hover:text-cafe-oliveDark transition-colors"
                        >
                            {malaysiaLinkLabel}
                        </Link>
                        {malaysiaLineTail}
                    </p>
                </div>

                {/* Mobile-only italic subcaption (desktop shows it in eyebrow row) */}
                <p className="md:hidden mt-3 font-display italic text-cafe-espresso/80 text-xl text-center">
                    {subCaption}
                </p>

                {/* System version badge kept at the very edge, muted */}
                <div className="mt-5 flex items-center justify-center opacity-55">
                    <SystemVersionBadge variant="hero" />
                </div>
            </div>
        </section>
    );
}

// ---------- Services ----------
function Services() {
    const { t, lang } = useT();
    const services = buildServices(t);
    return (
        <section id="services" className="pt-20 md:pt-28 pb-20 md:pb-28 bg-cafe-cream border-t border-cafe-espresso/8" data-testid="services-section">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14">
                {/* Header */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 mb-14 md:mb-20 items-end">
                    <div className="lg:col-span-7">
                        <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                            {lang === "cn" ? "我做什么" : "What we do"}
                        </p>
                        <h2 className="font-serif text-4xl md:text-6xl lg:text-[76px] text-cafe-espresso leading-[0.95] text-balance" style={{ fontWeight: 500 }}>
                            {lang === "cn" ? "影片、照片、Reel —" : "Film, stills, reels —"}<br />
                            <em className="font-display italic text-cafe-olive">{lang === "cn" ? "为餐饮品牌而拍。" : "shot for F&B brands."}</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-base md:text-lg text-cafe-espresso/70 leading-relaxed max-w-md">
                            {lang === "cn"
                                ? "从一支 Reel 到一场完整的品牌 Campaign,构建画面风格一致的视觉资产。"
                                : "From a single reel to a full brand campaign — we build visual content with one consistent look."}
                        </p>
                    </div>
                </div>

                {/* 5 service rows — editorial list, numbered left, serif title, short description + line
                    item details right. Hover reveals an olive underline. */}
                <div className="divide-y divide-cafe-espresso/10 border-t border-cafe-espresso/10" data-testid="services-list">
                    {services.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <Link
                                key={s.id}
                                to="/photo-video-services"
                                data-testid={`service-${s.id}`}
                                className="group grid grid-cols-12 gap-4 md:gap-6 items-center py-7 md:py-9 hover:bg-cafe-latte/40 transition-colors px-2 -mx-2"
                            >
                                <div className="col-span-2 md:col-span-1 font-display italic text-cafe-espresso/60 text-xl md:text-2xl">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <div className="col-span-10 md:col-span-5 flex items-center gap-4">
                                    <Icon className="h-5 w-5 md:h-6 md:w-6 text-cafe-olive shrink-0" strokeWidth={1.3} />
                                    <h3 className="font-serif text-2xl md:text-4xl text-cafe-espresso leading-tight" style={{ fontWeight: 500 }}>
                                        {s.title}
                                    </h3>
                                </div>
                                <div className="col-span-12 md:col-span-5 text-cafe-espresso/70 text-sm md:text-base leading-relaxed">
                                    {s.short}
                                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.18em] text-cafe-espresso/55">
                                        {s.details.map(d => <span key={d}>{d}</span>)}
                                    </div>
                                </div>
                                <div className="hidden md:flex md:col-span-1 justify-end">
                                    <ArrowUpRight className="h-5 w-5 text-cafe-espresso/40 group-hover:text-cafe-olive group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" strokeWidth={1.3} />
                                </div>
                            </Link>
                        );
                    })}
                </div>

                <div className="mt-12 md:mt-16 flex flex-wrap items-center justify-between gap-6">
                    <p className="text-cafe-espresso/60 text-sm italic font-display">
                        {t("services.list_hint")}
                    </p>
                    <div className="flex gap-3 flex-wrap">
                        <Link
                            to={lang === "cn" ? "/photo-video-services/cn#services-pricing" : "/photo-video-services#services-pricing"}
                            data-testid="services-see-rates"
                            className="inline-flex items-center h-11 px-6 rounded-full bg-cafe-espresso text-cafe-cream text-[13.5px] font-medium hover:bg-cafe-olive transition-colors"
                        >
                            {t("services.see_rates")}
                            <ArrowUpRight className="ml-1.5 h-4 w-4" strokeWidth={1.5} />
                        </Link>
                        <a
                            href="#contact"
                            className="inline-flex items-center h-11 px-6 rounded-full border border-cafe-espresso text-cafe-espresso text-[13.5px] font-medium hover:bg-cafe-espresso hover:text-cafe-cream transition-colors"
                        >
                            {t("services.start_brief")}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------- Behold widget loader ----------
// Compatibility embed per behold.so docs — we use `<div data-behold-id>` (not
// the <behold-widget> custom element) because React sometimes strips unknown
// custom elements during hydration. Script is loaded once per page.
const BEHOLD_FEED_ID = "RMZsKXMHDj9lBsGBxZQa"; // @kaily_gan — Feb 2026
function useBeholdWidget() {
    useEffect(() => {
        if (window.__bhldScript) return;
        window.__bhldScript = true;
        const s = document.createElement("script");
        s.type = "module";
        s.src = "https://w.behold.so/widget.js";
        setTimeout(() => { document.head.appendChild(s); }, 0);
    }, []);
}

// ---------- Work / Portfolio (Behold live IG feed) ----------
function Work() {
    const { t } = useT();
    useBeholdWidget();

    return (
        <section id="instagram-feed" className="py-24 md:py-32 bg-cafe-cream relative overflow-hidden border-t border-cafe-espresso/8">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14 relative">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
                    <div>
                        <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">{t("work.ig_overline")}</p>
                        <h2 className="font-serif text-4xl md:text-6xl lg:text-[72px] text-cafe-espresso leading-[0.95] text-balance" style={{ fontWeight: 500 }}>
                            <em className="font-display italic text-cafe-olive">@kaily_gan</em><br />
                            {t("work.ig_subtitle")}
                        </h2>
                    </div>
                    <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="group inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.26em] text-cafe-espresso/80 hover:text-cafe-olive border-b border-cafe-espresso/25 hover:border-cafe-olive pb-1 whitespace-nowrap transition-colors mb-2"
                        data-testid="work-ig-link"
                    >
                        {t("work.ig_see")}
                        <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </a>
                </div>

                {/* Feb 2026 — Behold feed re-bound to @kaily_gan
                    (feed id RMZsKXMHDj9lBsGBxZQa). Compatibility embed
                    (plain <div data-behold-id>) so React doesn't strip
                    the custom element. The static CTA pill underneath
                    remains as an honest fallback if the widget fails to
                    load (network blocked, feed unpublished, etc.). */}
                <div
                    data-behold-id={BEHOLD_FEED_ID}
                    data-testid="behold-widget"
                    className="min-h-[220px]"
                />

                <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="ig-callout"
                    className="group mt-8 inline-flex items-center gap-2 h-11 md:h-12 px-6 md:px-7 rounded-full border border-cafe-espresso text-cafe-espresso text-[13.5px] font-medium hover:bg-cafe-espresso hover:text-cafe-cream transition-colors">
                    {t("work.ig_open")}
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                </a>
            </div>
        </section>
    );
}

// ---------- Process ----------
function Process() {
    const { t } = useT();
    const steps = buildProcess(t);
    return (
        <section id="process" className="py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-14 md:mb-20">
                    <div className="text-xs md:text-sm uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-4">{t("process.overline")}</div>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.05] text-balance">
                        {t("process.title_1")}<em className="font-display italic">{t("process.title_2")}</em>{t("process.title_3")}
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12" data-testid="process-steps">
                    {steps.map((p) => (
                        <div key={p.step} className="relative">
                            <div className="font-display italic text-6xl md:text-7xl text-cafe-olive/30 leading-none">
                                {p.step}
                            </div>
                            <h3 className="mt-4 font-serif text-2xl md:text-3xl text-cafe-espresso">{p.title}</h3>
                            <p className="mt-3 text-cafe-mocha leading-relaxed">{p.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------- Contact Form ----------
function Contact() {
    const { t } = useT();
    const [form, setForm] = useState({
        name: "",
        business_name: "",
        email: "",
        phone: "",
        budget: "",
        timeline: "",
        message: "",
    });
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const serviceCards = buildServices(t);

    const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
    const toggleService = (title) =>
        setServices((prev) =>
            prev.includes(title) ? prev.filter((s) => s !== title) : [...prev, title]
        );

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                business_name: form.business_name.trim() || null,
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                services,
                budget: form.budget.trim() || null,
                timeline: form.timeline.trim() || null,
                message: form.message.trim(),
            };
            const { data } = await axios.post(`${API}/inquiries`, payload);
            toast.success("Inquiry received — terima kasih!", {
                description: `Kaily will reply within 24 hours. Ref: ${data.id.slice(0, 8)}`,
            });
            setDone(true);
            setForm({ name: "", business_name: "", email: "", phone: "", budget: "", timeline: "", message: "" });
            setServices([]);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail)
                ? detail.map((d) => d.msg || d).join(", ")
                : detail || "Please try again.";
            toast.error("Couldn't send inquiry", { description: String(msg) });
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "mt-2 h-12 bg-transparent border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50";

    return (
        <section id="contact" className="py-24 md:py-32 bg-cafe-latte/50 border-t border-cafe-espresso/8" data-testid="contact-section">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
                <div className="lg:col-span-2">
                    <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">{t("contact.overline")}</p>
                    <h2 className="font-serif text-4xl md:text-6xl lg:text-[72px] text-cafe-espresso leading-[0.95] text-balance" style={{ fontWeight: 500 }}>
                        {t("contact.title_1")}
                        <em className="font-display italic text-cafe-olive">{t("contact.title_2")}</em>.
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-cafe-mocha leading-relaxed max-w-md text-pretty">
                        {t("contact.body")}
                    </p>

                    <div className="mt-10 flex flex-col gap-4" data-testid="contact-details">
                        <a
                            href={WHATSAPP_LINK}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-3 text-cafe-espresso hover:text-cafe-olive transition-colors"
                            data-testid="contact-whatsapp"
                        >
                            <span className="h-10 w-10 rounded-full bg-cafe-latte flex items-center justify-center">
                                <MessageCircle className="h-4 w-4 text-cafe-olive" strokeWidth={1.75} />
                            </span>
                            <span>
                                <span className="block text-xs uppercase tracking-[0.2em] text-cafe-mocha">WhatsApp</span>
                                <span className="font-medium">{WHATSAPP_DISPLAY}</span>
                            </span>
                        </a>
                        <a
                            href={`mailto:${EMAIL}`}
                            className="flex items-center gap-3 text-cafe-espresso hover:text-cafe-olive transition-colors"
                            data-testid="contact-email"
                        >
                            <span className="h-10 w-10 rounded-full bg-cafe-latte flex items-center justify-center">
                                <Mail className="h-4 w-4 text-cafe-olive" strokeWidth={1.75} />
                            </span>
                            <span>
                                <span className="block text-xs uppercase tracking-[0.2em] text-cafe-mocha">Email</span>
                                <span className="font-medium">{EMAIL}</span>
                            </span>
                        </a>
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-3 text-cafe-espresso hover:text-cafe-olive transition-colors"
                            data-testid="contact-instagram"
                        >
                            <span className="h-10 w-10 rounded-full bg-cafe-latte flex items-center justify-center">
                                <Instagram className="h-4 w-4 text-cafe-olive" strokeWidth={1.75} />
                            </span>
                            <span>
                                <span className="block text-xs uppercase tracking-[0.2em] text-cafe-mocha">Instagram</span>
                                <span className="font-medium">@kaily_gan</span>
                            </span>
                        </a>
                    </div>
                </div>

                <div className="lg:col-span-3">
                    <form
                        onSubmit={onSubmit}
                        data-testid="inquiry-form"
                        className="bg-cafe-cream border border-border rounded-[2rem] p-6 md:p-10"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <Label htmlFor="inq-name" className="text-cafe-espresso font-medium">{t("contact.form.name")}</Label>
                                <Input id="inq-name" data-testid="inq-name" value={form.name} onChange={update("name")} placeholder={t("contact.form.name_ph")} className={inputCls} />
                            </div>
                            <div>
                                <Label htmlFor="inq-business" className="text-cafe-espresso font-medium">{t("contact.form.business")}</Label>
                                <Input id="inq-business" data-testid="inq-business" value={form.business_name} onChange={update("business_name")} placeholder={t("contact.form.business_ph")} className={inputCls} />
                            </div>
                            <div>
                                <Label htmlFor="inq-email" className="text-cafe-espresso font-medium">{t("contact.form.email")}</Label>
                                <Input id="inq-email" data-testid="inq-email" type="email" value={form.email} onChange={update("email")} placeholder={t("contact.form.email_ph")} className={inputCls} />
                            </div>
                            <div>
                                <Label htmlFor="inq-phone" className="text-cafe-espresso font-medium">{t("contact.form.phone")}</Label>
                                <Input id="inq-phone" data-testid="inq-phone" value={form.phone} onChange={update("phone")} placeholder={t("contact.form.phone_ph")} className={inputCls} />
                            </div>

                            <div className="md:col-span-2">
                                <Label className="text-cafe-espresso font-medium">{t("contact.form.interested")}</Label>
                                <div className="mt-3 flex flex-wrap gap-2" data-testid="service-chips">
                                    {serviceCards.map((s) => {
                                        const on = services.includes(s.title);
                                        return (
                                            <button
                                                type="button"
                                                key={s.id}
                                                data-testid={`chip-${s.id}`}
                                                onClick={() => toggleService(s.title)}
                                                className={cn(
                                                    "h-10 px-4 rounded-full border text-sm transition-all",
                                                    on
                                                        ? "bg-cafe-olive text-cafe-cream border-cafe-olive"
                                                        : "bg-transparent border-border text-cafe-espresso hover:border-cafe-olive"
                                                )}
                                            >
                                                {s.title}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="inq-budget" className="text-cafe-espresso font-medium">{t("contact.form.budget")}</Label>
                                <Input id="inq-budget" data-testid="inq-budget" value={form.budget} onChange={update("budget")} placeholder={t("contact.form.budget_ph")} className={inputCls} />
                            </div>
                            <div>
                                <Label htmlFor="inq-timeline" className="text-cafe-espresso font-medium">{t("contact.form.timeline")}</Label>
                                <Input id="inq-timeline" data-testid="inq-timeline" value={form.timeline} onChange={update("timeline")} placeholder={t("contact.form.timeline_ph")} className={inputCls} />
                            </div>

                            <div className="md:col-span-2">
                                <Label htmlFor="inq-message" className="text-cafe-espresso font-medium">{t("contact.form.message")}</Label>
                                <Textarea
                                    id="inq-message"
                                    data-testid="inq-message"
                                    value={form.message}
                                    onChange={update("message")}
                                    rows={4}
                                    placeholder={t("contact.form.message_ph")}
                                    className="mt-2 bg-transparent border border-border rounded-xl focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50"
                                />
                            </div>
                        </div>

                        <div className="mt-8 flex items-center justify-between gap-4">
                            <p className="text-xs text-cafe-mocha max-w-xs">
                                {t("contact.form.consent")}
                            </p>
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid="inquiry-submit"
                                className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-12 px-8 disabled:opacity-60"
                            >
                                {loading ? t("contact.form.submitting") : done ? t("contact.form.sent") : t("contact.form.submit")}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

// ---------- Working with Kaily ----------
function WorkingWithKaily() {
    const { t } = useT();
    const points = [
        { title: t("wwk.point.1.title"), body: t("wwk.point.1.body") },
        { title: t("wwk.point.2.title"), body: t("wwk.point.2.body") },
        { title: t("wwk.point.3.title"), body: t("wwk.point.3.body") },
        { title: t("wwk.point.4.title"), body: t("wwk.point.4.body") },
    ];
    return (
        <section id="working-with-kaily" className="py-24 md:py-32 border-t border-cafe-espresso/8" data-testid="working-with-kaily-section">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 md:mb-20 items-end">
                    <div className="lg:col-span-7">
                        <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">{t("wwk.overline")}</p>
                        <h2 className="font-serif text-4xl md:text-6xl lg:text-[72px] text-cafe-espresso leading-[0.95] text-balance" style={{ fontWeight: 500 }}>
                            {t("wwk.title_1")}<em className="font-display italic text-cafe-olive">{t("wwk.title_2")}</em>.
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-base md:text-lg text-cafe-espresso/70 leading-relaxed max-w-md">
                            {t("wwk.intro")}
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 md:gap-y-14 gap-x-14" data-testid="working-with-kaily-points">
                    {points.map((p, i) => (
                        <div key={p.title} className="relative pl-10">
                            <span className="absolute left-0 top-1 font-display italic text-cafe-olive/80 text-xl">{String(i + 1).padStart(2, "0")}</span>
                            <h3 className="font-serif text-2xl md:text-3xl text-cafe-espresso mb-2 leading-tight" style={{ fontWeight: 500 }}>{p.title}</h3>
                            <p className="text-cafe-espresso/70 leading-relaxed">{p.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------- International Collaboration ----------
// Reuses the shared block component; the "Start an International Brief"
// CTA deep-links to the brief form anchor on /international so a tap
// scrolls straight to the form.
function InternationalCollab() {
    return <InternationalCollabBlock ctaHref="/international#brief" id="international" />;
}

export default function Landing() {
    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso" data-testid="landing-page">
            <SiteNav />
            <main>
                <Hero />
                <RecentWork />
                <Services />
                <Work />
                <WorkingWithKaily />
                <InternationalCollab />
                <Process />
                <Contact />
            </main>
            <SiteFooter />
            <WhatsAppFab />
            <StickyCTA />
        </div>
    );
}
